import { CSSProperties } from '@material-ui/core/styles/withStyles';
import React from 'react';
import { Temp, Fall, WeatherConditions } from './Weather';
import WeatherIcon from './WeatherIcon';

type WeatherTableProps = {
	weatherConditions?: WeatherConditions[];
	showWeatherDetails: boolean;
};

const tableBackgroundColor = '#e5e7eb';
const tableBorder = `2px solid ${tableBackgroundColor}`;
const spacing = '8px';
const tdStyle: CSSProperties = { textAlign: 'center', borderRight: tableBorder };
const tdContainerStyle: CSSProperties = { backgroundColor: tableBackgroundColor, padding: spacing };
const textDarkGray400: CSSProperties = { color: '#3c4858' };
const textGray400: CSSProperties = { color: '#9ca3af' };
const textRed700: CSSProperties = { color: '#ef4444', fontWeight: 700 };
const tableStyle: CSSProperties = {
	...textDarkGray400,
	borderSpacing: '0',
	border: tableBorder,
	borderRight: 'none',
	margin: '0 auto',
	borderRadius: '6px',
	width: '100%',
};

export default function WeatherTable({ weatherConditions, showWeatherDetails }: WeatherTableProps) {
	let descriptions: string[] = [];
	let icons: string[] = [];
	let pops: (number | undefined)[] = [];
	let snows: (number | Fall | undefined)[] = [];
	let rains: (number | Fall | undefined)[] = [];
	let temps: (number | Temp)[] = [];
	let dts: number[] = [];
	weatherConditions?.map((dailyWeather) => {
		const { icon, description } = dailyWeather.weather[0];
		const { pop, temp, dt, snow, rain } = dailyWeather;
		descriptions.push(description);
		icons.push(icon);
		pops.push(pop);
		rains.push(rain);
		snows.push(snow);
		temps.push(temp);
		dts.push(dt);
	});

	return (
		<table style={tableStyle}>
			<thead>
				<tr>
					<td style={tdContainerStyle}>Date</td>
					{dts.map((d, idx) => {
						const fmtDate = (dt: number) => new Date(dt * 1000).toLocaleDateString('en-GB').slice(0, 5);
						return (
							<td style={tdStyle} key={`wt-td-${idx}`}>
								<div>{fmtDate(d)}</div>
							</td>
						);
					})}
				</tr>
			</thead>
			<tbody>
				<tr>
					<td style={tdContainerStyle}>Conditions</td>
					{descriptions.map((desc, idx) => {
						return (
							<td style={tdStyle} key={`wd-td-2-${idx}`}>
								<div style={{ width: '60%', minWidth: '100px', margin: '0 auto' }}>
									<WeatherIcon description={desc} icon={icons[idx]} />
								</div>
							</td>
						);
					})}
				</tr>
				<tr>
					<td style={tdContainerStyle}>Fall chance</td>
					{pops.map((pop, idx) => {
						const fmtPop = pop ? (pop * 100).toFixed(0) + '%' : '0%';
						const popHighlight = (pop: number | undefined) => {
							const threshold = 70;
							const popPercent = pop * 100;
							if (!pop) return null;
							if (popPercent >= threshold) return textRed700;
						};
						return (
							<td style={tdStyle} key={`wd-td-3-${idx}`}>
								<div style={popHighlight(pop)}>{fmtPop}</div>
							</td>
						);
					})}
				</tr>
				<tr>
					<td style={tdContainerStyle}>Rain</td>
					{rains.map((rain, idx) => {
						const rainValue = typeof rain === "number"
							? rain
								? rain < 1
									? '<1'
									: Math.round(rain)
								: 0
							: 0;
						return (
							<td style={tdStyle} key={`wt-td-rain-${idx}`}>
								<div> { rainValue }mm </div>
							</td>
						);
					})}
				</tr>
				<tr style={{ display: 'table-row' }}>
					<td style={tdContainerStyle}>Snow</td>
					{snows.map((snow, idx) => {
						const snowValue = typeof snow === "number"
						? snow
							? snow < 1
								? '<1'
								: Math.round(snow)
							: 0
						: 0;
						return (
							<td style={tdStyle} key={`wt-td-snow-${idx}`}>
								<div> { snowValue }mm </div>
							</td>
						);
					})}
				</tr>
				<tr style={{ display: 'table-row' }}>
					<td style={tdContainerStyle}>Temperature [max/min]</td>
					{temps.map((temp, idx) => {
						const fmtTempMax = (temp: number | Temp) => {
							const fmtTmp = typeof temp === 'number' ? temp : temp.max;
							return Math.round(fmtTmp as number);
						};
						const fmtTempMin = (temp: number | Temp) => {
							const fmtTmp = typeof temp === 'number' ? temp : temp.min;
							return Math.round(fmtTmp as number);
						};
						return (
							<td style={{ ...tdStyle, ...textGray400 }} key={`wt-td-temp-${idx}`}>
								<div>{`${fmtTempMax(temp)}°C / ${fmtTempMin(temp)}°C`}</div>
							</td>
						);
					})}
				</tr>
			</tbody>
		</table>
	);
}
